import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { SynthLoop, connectElement } from "../lib/audio";
import { isAlly } from "../lib/theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const UPLOAD_LABELS = {
  main: "Transmission locked in",
  ally: "Ally anthem locked in",
  ally_fx: "Ally button sound locked in",
  feast_fx: "Feast sound locked in",
};

export const MusicPlayer = () => {
  const [track, setTrack] = useState(null);
  const [slot, setSlot] = useState(() => (isAlly() ? "ally" : "main"));
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.6);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const audioRef = useRef(null);
  const synthRef = useRef(null);
  const audioInputRef = useRef(null);
  const allyInputRef = useRef(null);
  const fxInputRef = useRef(null);
  const feastFxInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const playingRef = useRef(false);
  playingRef.current = playing;
  const trackRef = useRef(track);
  trackRef.current = track;
  const resumeRef = useRef(false);

  // feast clips duck the background track while they play
  useEffect(() => {
    const onPause = () => {
      if (!playingRef.current) return;
      resumeRef.current = true;
      if (trackRef.current && audioRef.current) audioRef.current.pause();
      else synthRef.current?.stop();
      setPlaying(false);
    };
    const onResume = () => {
      if (!resumeRef.current) return;
      resumeRef.current = false;
      startRef.current(true);
    };
    window.addEventListener("rotu:music-pause", onPause);
    window.addEventListener("rotu:music-resume", onResume);
    return () => {
      window.removeEventListener("rotu:music-pause", onPause);
      window.removeEventListener("rotu:music-resume", onResume);
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/music?slot=${slot}`).then((res) => setTrack(res.data.track)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    synthRef.current?.setVolume(volume);
  }, [volume]);

  // owner-only: U = main track, A = ally anthem, S = ally button sound, F = feast sound, V = intro film, I = lineup image
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")) return;
      const k = e.key.toLowerCase();
      if (k === "u") audioInputRef.current?.click();
      if (k === "a") allyInputRef.current?.click();
      if (k === "s") fxInputRef.current?.click();
      if (k === "f") feastFxInputRef.current?.click();
      if (k === "v") videoInputRef.current?.click();
      if (k === "i") imageInputRef.current?.click();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const startPlayback = async (silent = false) => {
    if (track) {
      try {
        connectElement(audioRef.current);
        await audioRef.current.play();
        setPlaying(true);
      } catch (e) {
        if (!silent) toast.error("Playback blocked. Tap again.");
      }
    } else {
      if (!synthRef.current) synthRef.current = new SynthLoop();
      synthRef.current.setVolume(volume);
      synthRef.current.start();
      setPlaying(true);
    }
  };
  const startRef = useRef(startPlayback);
  startRef.current = startPlayback;

  // auto-start the transmission once the intro film finishes or is skipped
  useEffect(() => {
    const onEnter = () => {
      if (!playingRef.current) startRef.current(true);
    };
    window.addEventListener("rotu:enter", onEnter);
    return () => window.removeEventListener("rotu:enter", onEnter);
  }, []);

  // swap soundtrack when the theme flips; entering ally always auto-plays
  useEffect(() => {
    const onTheme = (e) => {
      const s = e.detail.ally ? "ally" : "main";
      setSlot(s);
      const wasPlaying = playingRef.current;
      if (audioRef.current) audioRef.current.pause();
      synthRef.current?.stop();
      setPlaying(false);
      axios
        .get(`${API}/music?slot=${s}`)
        .then((res) => {
          setTrack(res.data.track);
          if (wasPlaying || e.detail.ally) setTimeout(() => startRef.current(true), 200);
        })
        .catch(() => {});
    };
    window.addEventListener("rotu:theme", onTheme);
    return () => window.removeEventListener("rotu:theme", onTheme);
  }, []);

  const toggle = () => {
    if (playing) {
      if (track && audioRef.current) audioRef.current.pause();
      else synthRef.current?.stop();
      setPlaying(false);
    } else {
      startPlayback();
    }
  };

  const upload = async (e, targetSlot) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/music/upload?slot=${targetSlot}`, fd);
      if (targetSlot === slot) {
        synthRef.current?.stop();
        setTrack(res.data);
        setPlaying(false);
      }
      toast.success(`${UPLOAD_LABELS[targetSlot] || "Locked in"}: ${res.data.name}`);
    } catch (err) {
      toast.error("Upload rejected. Use mp3, wav, ogg, m4a or flac.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/image/upload?slot=lineup`, fd);
      toast.success(`Lineup visual locked in: ${res.data.name}`);
    } catch (err) {
      toast.error("Image rejected. Use jpg, png, webp or gif.");
    } finally {
      e.target.value = "";
    }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/video/upload`, fd);
      toast.success(`Intro film locked in: ${res.data.name}`);
    } catch (err) {
      toast.error("Film rejected. Use mp4, webm, mov or ogv.");
    } finally {
      setUploadingVideo(false);
      e.target.value = "";
    }
  };

  return (
    <div
      className={`fixed top-3 left-1/2 -translate-x-1/2 z-[55] flex items-center gap-1.5 border border-white/15 bg-[#08080C]/85 backdrop-blur-xl rounded-full pl-2.5 pr-1 py-1 ${playing ? "" : "eq-paused"}`}
      data-testid="music-player"
      title={track ? track.name : "SEWER BASS 132"}
    >
      {track && (
        <audio
          ref={audioRef}
          src={`${BACKEND}${track.url}`}
          loop
          onError={() => {
            setTrack(null);
            setPlaying(false);
          }}
        />
      )}

      <div className="flex items-end gap-[2px] h-3 w-6" data-testid="music-visualizer">
        {[0.9, 0.5, 1.1, 0.7, 1.3, 0.6, 1.0, 0.8].map((d, i) => (
          <span
            key={i}
            className="eq-bar w-[2px] h-full bg-[#39FF14]"
            style={{ animationDuration: `${d}s`, animationDelay: `${i * 0.08}s` }}
          />
        ))}
      </div>

      <span
        className="hidden lg:block text-[9px] tracking-[0.25em] uppercase text-zinc-500 max-w-[90px] truncate"
        data-testid="music-track-name"
      >
        {track ? track.name : "SEWER BASS 132"}
      </span>

      <button
        onClick={toggle}
        data-testid="music-play-button"
        className="w-6 h-6 rounded-full border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#08080C] transition-colors duration-200 flex items-center justify-center shrink-0"
        aria-label={playing ? "Pause music" : "Play music"}
      >
        {playing ? <Pause size={10} /> : <Play size={10} className="ml-0.5" />}
      </button>

      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        onChange={(e) => upload(e, "main")}
        className="hidden"
        data-testid="music-upload-input"
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={allyInputRef}
        type="file"
        accept="audio/*"
        onChange={(e) => upload(e, "ally")}
        className="hidden"
        data-testid="ally-upload-input"
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={fxInputRef}
        type="file"
        accept="audio/*"
        onChange={(e) => upload(e, "ally_fx")}
        className="hidden"
        data-testid="fx-upload-input"
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={feastFxInputRef}
        type="file"
        accept="audio/*"
        onChange={(e) => upload(e, "feast_fx")}
        className="hidden"
        data-testid="feast-fx-upload-input"
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={uploadImage}
        className="hidden"
        data-testid="image-upload-input"
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={uploadVideo}
        className="hidden"
        data-testid="video-upload-input"
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  );
};
